import React, { useState, useEffect, useRef } from 'react';
import { FiCalendar, FiChevronLeft, FiChevronRight, FiSearch, FiFilter } from 'react-icons/fi';
import { format, isToday, parseISO, addDays, isAfter, isBefore, isSameDay, isValid, subDays, startOfMonth, endOfMonth, getMonth, getYear } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import './DateCarousel.css';
import { useTheme } from '../../../contexts/ThemeContext';
import { toast } from 'react-hot-toast';

/**
 * Componente DateCarousel
 * 
 * Este componente exibe um carrossel de datas para navegação no diário de obras.
 * O modo escuro (darkMode) é recebido como prop do componente pai, que usa o ThemeContext.
 */
const DateCarousel = ({ 
  dataInicial, 
  dataFinal = new Date().toISOString(), 
  onDateSelect, 
  dataSelecionada = new Date().toISOString(),
  darkMode = false
}) => {
  const scrollContainerRef = useRef(null);
  const [selectedDate, setSelectedDate] = useState(dataSelecionada || new Date().toISOString());
  const [visibleDates, setVisibleDates] = useState([]);
  const [startDate, setStartDate] = useState(dataInicial);
  const [endDate, setEndDate] = useState(dataFinal);
  const [error, setError] = useState(null);
  const [dataFiltro, setDataFiltro] = useState('');
  const [showFiltro, setShowFiltro] = useState(false);
  const [showFiltroMes, setShowFiltroMes] = useState(false);
  const [mesSelecionado, setMesSelecionado] = useState(getMonth(new Date()));
  const [anoSelecionado, setAnoSelecionado] = useState(getYear(new Date()));
  const [modoFiltro, setModoFiltro] = useState('carrossel'); // 'carrossel' ou 'mes'
  
  // Estados para controle do drag
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [dragDistance, setDragDistance] = useState(0);
  const carouselRef = useRef(null);
  const { darkMode: themeDarkMode } = useTheme();

  // Função para validar uma data
  const isValidDate = (dateString) => {
    if (!dateString) return false;
    
    try {
      const date = parseISO(dateString);
      return !isNaN(date.getTime());
    } catch (error) {
      console.error("Erro ao validar data:", error);
      return false;
    }
  };

  // Função para formatar a data para o input
  const formatarDataParaInput = (dateString) => {
    try {
      const date = parseISO(dateString);
      return format(date, 'dd/MM/yyyy');
    } catch (error) {
      return '';
    }
  };

  // Função para converter a data do input para ISO
  const converterDataDoInput = (dataInput) => {
    try {
      // Remover caracteres não numéricos
      const numeros = dataInput.replace(/\D/g, '');
      
      // Se não tiver 8 dígitos, retornar vazio
      if (numeros.length !== 8) return '';
      
      // Separar dia, mês e ano
      const dia = numeros.substring(0, 2);
      const mes = numeros.substring(2, 4);
      const ano = numeros.substring(4, 8);
      
      // Criar data usando UTC para evitar problemas de fuso horário
      const data = new Date(Date.UTC(ano, mes - 1, dia));
      if (!isValid(data)) return '';
      
      // Formatar a data mantendo o dia correto usando UTC
      return `${ano}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`;
    } catch (error) {
      console.error('Erro ao converter data:', error);
      return '';
    }
  };

  // Função para lidar com a mudança no input de data
  const handleDataFiltroChange = (e) => {
    const valor = e.target.value;
    
    // Formatar o valor enquanto digita
    let valorFormatado = valor.replace(/\D/g, '');
    if (valorFormatado.length > 2) {
      valorFormatado = valorFormatado.substring(0, 2) + '/' + valorFormatado.substring(2);
    }
    if (valorFormatado.length > 5) {
      valorFormatado = valorFormatado.substring(0, 5) + '/' + valorFormatado.substring(5, 9);
    }
    
    setDataFiltro(valorFormatado);
  };

  // Função para aplicar o filtro de data
  const aplicarFiltroData = () => {
    const dataISO = converterDataDoInput(dataFiltro);
    
    if (!dataISO) {
      toast.error('Data inválida. Use o formato DD/MM/AAAA');
      return;
    }

    const dataFiltrada = parseISO(dataISO);
    const dataInicio = parseISO(startDate);
    const dataFim = parseISO(endDate);

    if (isBefore(dataFiltrada, dataInicio) || isAfter(dataFiltrada, dataFim)) {
      toast.error('Data fora do período da obra');
      return;
    }

    // Verificar se a data filtrada está dentro dos últimos 30 dias
    const dataLimite = subDays(new Date(), 30);
    if (isBefore(dataFiltrada, dataLimite)) {
      toast.info('Data selecionada está antes dos últimos 30 dias. O carrossel mostrará apenas os últimos 30 dias.');
    }

    handleDateSelect(dataISO);
    setShowFiltro(false);
  };

  // Função para lidar com a tecla Enter no input
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      aplicarFiltroData();
    }
  };

  // Função para gerar array de meses disponíveis
  const gerarMesesDisponiveis = () => {
    const meses = [];
    const dataInicio = parseISO(startDate);
    const dataFim = parseISO(endDate);
    let dataAtual = startOfMonth(dataInicio);

    while (!isAfter(dataAtual, dataFim)) {
      const mes = getMonth(dataAtual);
      const ano = getYear(dataAtual);
      const chave = `${ano}-${mes}`;
      
      if (!meses.some(m => m.chave === chave)) {
        meses.push({
          chave,
          mes,
          ano,
          label: format(dataAtual, 'MMMM/yyyy', { locale: ptBR })
        });
      }
      
      dataAtual = addDays(dataAtual, 32); // Avança para o próximo mês
    }

    return meses;
  };

  // Função para aplicar filtro por mês
  const aplicarFiltroMes = () => {
    const dataInicio = parseISO(startDate);
    const dataFim = parseISO(endDate);
    
    // Criar data do primeiro dia do mês selecionado
    const dataFiltro = new Date(anoSelecionado, mesSelecionado, 1);
    
    // Verificar se a data está dentro do período da obra
    if (isBefore(dataFiltro, dataInicio) || isAfter(dataFiltro, dataFim)) {
      toast.error('Mês selecionado está fora do período da obra');
      return;
    }

    setModoFiltro('mes');
    setShowFiltroMes(false);
  };

  // Gerar array de datas visíveis
  useEffect(() => {
    try {
      setError(null);
      
      let startDateToUse = startDate;
      let endDateToUse = endDate;
      
      if (!startDateToUse || !isValidDate(startDateToUse)) {
        console.warn("Data inicial inválida ou não fornecida, usando data atual");
        startDateToUse = new Date().toISOString();
        setStartDate(startDateToUse);
      }
      
      if (!endDateToUse || !isValidDate(endDateToUse)) {
        console.warn("Data final inválida ou não fornecida, usando data atual");
        endDateToUse = new Date().toISOString();
        setEndDate(endDateToUse);
      }

      const start = startDateToUse ? parseISO(startDateToUse) : new Date();
      const end = endDateToUse ? parseISO(endDateToUse) : new Date();
      
      if (isAfter(start, end)) {
        console.error('Data inicial não pode ser posterior à data final');
        setError('Data inicial posterior à data final');
        setStartDate(endDateToUse);
        setEndDate(startDateToUse);
        return;
      }

      let dateArray = [];
      
      if (modoFiltro === 'carrossel') {
        // Modo carrossel: últimos 31 dias
        const dataLimite = subDays(new Date(), 31);
        const dataInicioCarrossel = isBefore(start, dataLimite) ? dataLimite : start;
        let currentDate = dataInicioCarrossel;
        const maxDays = 31;
        let dayCount = 0;

        while (!isAfter(currentDate, end) && dayCount < maxDays) {
          dateArray.push(format(currentDate, 'yyyy-MM-dd'));
          currentDate = addDays(currentDate, 1);
          dayCount++;
        }
      } else {
        // Modo mês: todos os dias do mês selecionado
        const primeiroDiaMes = startOfMonth(new Date(anoSelecionado, mesSelecionado, 1));
        const ultimoDiaMes = endOfMonth(primeiroDiaMes);
        let currentDate = primeiroDiaMes;

        while (!isAfter(currentDate, ultimoDiaMes)) {
          // Só adiciona a data se estiver dentro do período da obra
          if (!isBefore(currentDate, start) && !isAfter(currentDate, end)) {
            dateArray.push(format(currentDate, 'yyyy-MM-dd'));
          }
          currentDate = addDays(currentDate, 1);
        }
      }

      setVisibleDates(dateArray);
    } catch (error) {
      console.error('Erro ao processar datas:', error);
      setError('Erro ao processar datas');
      setVisibleDates([format(new Date(), 'yyyy-MM-dd')]);
    }
  }, [startDate, endDate, modoFiltro, mesSelecionado, anoSelecionado]);

  // Função para centralizar a data selecionada
  const centralizarDataSelecionada = (date) => {
    if (!carouselRef.current || !date) return;

    try {
      // Aguardar o próximo ciclo de renderização para garantir que o DOM foi atualizado
      setTimeout(() => {
        const selectedElement = carouselRef.current.querySelector(`.date-item[data-date="${date}"]`);
        if (!selectedElement) return;

        const containerWidth = carouselRef.current.offsetWidth;
        const elementLeft = selectedElement.offsetLeft;
        const elementWidth = selectedElement.offsetWidth;
        
        // Calcular a posição para centralizar
        const scrollPosition = elementLeft - (containerWidth / 2) + (elementWidth / 2);
        
        // Verificar se é a primeira ou última data
        const isFirstDate = date === visibleDates[0];
        const isLastDate = date === visibleDates[visibleDates.length - 1];
        
        // Se for a primeira data, alinhar à esquerda
        // Se for a última data, alinhar à direita
        // Caso contrário, centralizar
        const finalScrollPosition = isFirstDate ? 0 : 
                                  isLastDate ? carouselRef.current.scrollWidth - containerWidth :
                                  Math.max(0, scrollPosition);

        // Usar requestAnimationFrame para garantir que a rolagem aconteça após a atualização visual
        requestAnimationFrame(() => {
          carouselRef.current.scrollTo({
            left: finalScrollPosition,
            behavior: 'smooth'
          });
        });
      }, 0);
    } catch (error) {
      console.error('Erro ao centralizar data:', error);
    }
  };

  const handleDateSelect = (date) => {
    try {
      if (!date || !isValidDate(date)) {
        console.error("Data inválida:", date);
        return;
      }
      
      // Atualizar o estado da data selecionada
      setSelectedDate(date);
      
      // Centralizar a data após a atualização do estado
      centralizarDataSelecionada(date);
      
      // Notificar o componente pai sobre a seleção
      if (onDateSelect) {
        onDateSelect(date);
      }
    } catch (error) {
      console.error('Erro ao selecionar data:', error);
    }
  };

  const handleScrollLeft = () => {
    try {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Erro ao rolar para esquerda:', error);
    }
  };

  const scrollRight = () => {
    try {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Erro ao rolar para direita:', error);
    }
  };

  // Funções para controle do drag
  const handleMouseDown = (e) => {
    if (!scrollContainerRef.current) return;
    
    setIsMouseDown(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
    setDragDistance(0);
    
    // Mudar o cursor apenas para grabbing durante o drag
    scrollContainerRef.current.style.cursor = 'grabbing';
    scrollContainerRef.current.style.userSelect = 'none';
  };

  const handleMouseMove = (e) => {
    if (!isMouseDown || !scrollContainerRef.current) return;
    
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Velocidade do scroll
    
    // Atualizar a distância do drag
    const currentDragDistance = Math.abs(walk);
    setDragDistance(currentDragDistance);
    
    // Se a distância for maior que 5 pixels, considerar como drag
    if (currentDragDistance > 5) {
      setIsDragging(true);
    }
    
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = (e) => {
    if (!scrollContainerRef.current) return;
    
    // Resetar todos os estados relacionados ao drag
    setIsDragging(false);
    setIsMouseDown(false);
    setDragDistance(0);
    setStartX(0);
    setScrollLeft(0);
    
    // Restaurar o cursor para o padrão
    scrollContainerRef.current.style.cursor = 'default';
    scrollContainerRef.current.style.userSelect = 'auto';
  };

  const handleMouseLeave = () => {
    if (!scrollContainerRef.current) return;
    
    // Resetar todos os estados relacionados ao drag
    setIsDragging(false);
    setIsMouseDown(false);
    setDragDistance(0);
    setStartX(0);
    setScrollLeft(0);
    
    // Restaurar o cursor para o padrão
    scrollContainerRef.current.style.cursor = 'default';
    scrollContainerRef.current.style.userSelect = 'auto';
  };

  const handleDateClick = (e, date) => {
    // Se o mouse ainda estiver pressionado ou se acabou de terminar um drag, não processar o click
    if (isMouseDown || dragDistance > 5) {
      return;
    }
    
    // Resetar estados de drag antes de processar o click
    setIsDragging(false);
    setDragDistance(0);
    
    // Usar handleDateSelect que agora inclui a centralização
    handleDateSelect(date);
  };

  // Se não houver datas visíveis, mostrar uma mensagem ou retornar null
  if (visibleDates.length === 0) {
    return (
      <div className={`date-carousel-container ${darkMode ? 'dark-mode' : ''}`}>
        <div className="date-carousel-header">
          <h3 className="date-carousel-title">
            <FiCalendar /> Selecionar Data
          </h3>
        </div>
        {error && <div className="date-carousel-error">{error}</div>}
        <div className="dates-container">
          <div className="date-item">
            <span className="date-day">-</span>
            <span className="date-month">-</span>
            <span className="date-year">-</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`date-carousel-container ${darkMode || themeDarkMode ? 'dark-mode' : ''}`}>
      <div className="date-carousel-header">
        <h3 className="date-carousel-title">
          <FiCalendar /> Selecionar Data
        </h3>
        <div className="date-carousel-actions">
          <button 
            className="btn-filtro-data"
            onClick={() => setShowFiltro(!showFiltro)}
            title="Filtrar por data específica"
          >
            <FiSearch />
          </button>
          <button 
            className="btn-filtro-mes"
            onClick={() => setShowFiltroMes(!showFiltroMes)}
            title="Filtrar por mês"
          >
            <FiFilter />
          </button>
          <div className="date-navigation">
            <button
              className="nav-button"
              onClick={handleScrollLeft}
              aria-label="Rolar para esquerda"
            >
              <FiChevronLeft />
            </button>
            <button
              className="nav-button"
              onClick={scrollRight}
              aria-label="Rolar para direita"
            >
              <FiChevronRight />
            </button>
          </div>
        </div>
      </div>

      {showFiltroMes && (
        <div className="filtro-mes-container">
          <div className="filtro-mes-input-group">
            <select
              value={mesSelecionado}
              onChange={(e) => setMesSelecionado(Number(e.target.value))}
              className="filtro-mes-select"
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i} value={i}>
                  {format(new Date(2000, i, 1), 'MMMM', { locale: ptBR })}
                </option>
              ))}
            </select>
            <select
              value={anoSelecionado}
              onChange={(e) => setAnoSelecionado(Number(e.target.value))}
              className="filtro-ano-select"
            >
              {Array.from(
                { length: getYear(parseISO(endDate)) - getYear(parseISO(startDate)) + 1 },
                (_, i) => getYear(parseISO(startDate)) + i
              ).map((ano) => (
                <option key={ano} value={ano}>
                  {ano}
                </option>
              ))}
            </select>
            <button 
              className="btn-aplicar-filtro"
              onClick={aplicarFiltroMes}
            >
              Aplicar
            </button>
            <button 
              className="btn-voltar-carrossel"
              onClick={() => {
                setModoFiltro('carrossel');
                setShowFiltroMes(false);
              }}
            >
              Voltar ao Carrossel
            </button>
          </div>
        </div>
      )}

      {showFiltro && (
        <div className="filtro-data-container">
          <div className="filtro-data-input-group">
            <input
              type="text"
              value={dataFiltro}
              onChange={handleDataFiltroChange}
              onKeyPress={handleKeyPress}
              placeholder="DD/MM/AAAA"
              maxLength={10}
              className="filtro-data-input"
            />
            <button 
              className="btn-aplicar-filtro"
              onClick={aplicarFiltroData}
            >
              Aplicar
            </button>
          </div>
          <small className="filtro-data-help">
            Digite a data desejada no formato DD/MM/AAAA
          </small>
        </div>
      )}

      {error && <div className="date-carousel-error">{error}</div>}
      
      <div 
        className={`dates-container ${isDragging ? 'dragging' : ''}`}
        ref={scrollContainerRef}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{ cursor: isDragging ? 'grabbing' : 'default' }}
      >
        {visibleDates.map((date) => {
          if (!date || !isValidDate(date)) return null;
          
          try {
            const formattedDate = parseISO(date);
            const day = format(formattedDate, 'd');
            const month = format(formattedDate, 'MMM', { locale: ptBR });
            const year = format(formattedDate, 'yyyy');
            const isCurrentDay = isToday(formattedDate);
            const isSelected = selectedDate && isSameDay(formattedDate, parseISO(selectedDate));

            return (
              <div
                key={date}
                data-date={date}
                className={`date-item ${isSelected ? 'selected active' : ''} ${isCurrentDay ? 'today' : ''}`}
                onClick={(e) => handleDateClick(e, date)}
                style={{ transition: 'all 0.2s ease' }}
              >
                <span className="date-day">{day}</span>
                <span className="date-month">{month}</span>
                <span className="date-year">{year}</span>
              </div>
            );
          } catch (error) {
            console.error('Erro ao processar data:', date, error);
            return null;
          }
        })}
      </div>
    </div>
  );
};

export default DateCarousel; 